import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import { fetchDiningSessionOrderItems } from "../services/ApiCalls";
import { opmStorage, Keys, tableScopeOf } from "../util/opmStorage";

/**
 * The whole table's bill from the server: every diner's dishes, the total, what was paid
 * and what remains. Needs the table's token, so it is null until the guest entered the
 * table code (`signedIn`), while it loads, and when the request failed (`failed`; `reload`
 * asks again).
 */
export default function useTableBill() {
  const { authToken, authClaims, diningSessionId, language, setTableNumbers } = useAppContext();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const restaurantId = authClaims?.restaurant_id != null ? String(authClaims.restaurant_id) : null;
  const elementId = tableScopeOf(authClaims);
  const sessionId = Number(authClaims?.dining_session_id ?? diningSessionId);
  const hasSession = Boolean(restaurantId && elementId && Number.isInteger(sessionId) && sessionId > 0);
  const token = hasSession ? authToken || opmStorage.getWithTtl(Keys.token(restaurantId, elementId)) : null;

  useEffect(() => {
    setFailed(false);
    if (!token) {
      setBill(null);
      setTableNumbers([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchDiningSessionOrderItems(restaurantId, sessionId, token, language === "en" ? "en" : "he")
      .then((data) => {
        if (cancelled) return;
        setBill(data || null);
        setTableNumbers((data?.tables || []).map((table) => table.element_number));
      })
      .catch(() => {
        if (cancelled) return;
        setBill(null);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId, sessionId, token, language, attempt, setTableNumbers]);

  return { bill, loading, failed, signedIn: Boolean(token), reload };
}
